import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import AppUtil from "utilities/AppUtil";
import httpRequest from "../../services/httpRequest";
import snakecaseKeys from "snakecase-keys";

// Main URL Endpoint
const _urlPrefix = "/product";

export const fetchProducts = createAsyncThunk(
  "product/fetchProducts",
  async (params) => {
    params = AppUtil.toSnakeCaseKey(params);
    const result = await httpRequest({
      url: _urlPrefix + "/page",
      method: "GET",
      params
    });
    if (!result) {
      return;
    }
    // dispatch(setProductQueries(params));
    AppUtil.scrollToTopById("main-panel");
    return result.data;
  }
);
export const fetchAllProducts = createAsyncThunk(
  "product/fetchAllProducts",
  async (params) => {
    params = AppUtil.toSnakeCaseKey(params);
    const result = await httpRequest({
      url: _urlPrefix + "/page",
      method: "GET",
      params
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);
export const fetchProductDetail = createAsyncThunk(
  "product/fetchProductDetail",
  async (params) => {
    const result = await httpRequest({
      url: _urlPrefix + "/detail/" + params,
      method: "GET"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);
export const reduceCreateProduct = createAsyncThunk(
  "product/createProduct",
  async (params, { dispatch, getState }) => {
    const { product, files } = params;
    const data = new FormData();
    for (let item of files) {
      data.append("files", item.file);
    }
    data.append(
      "product",
      new Blob([JSON.stringify(snakecaseKeys(product))], {
        type: "application/json",
      })
    );
    const result = await httpRequest({
      url: _urlPrefix + "/add",
      method: "POST",
      data: data,
      headers: {
        "Content-Type": "multipart/form-data",
      },
      successMessage: "toast.create.200"
    });
    if (!result) {
      return;
    }

    const queries = await getState().product.queries;
    dispatch(fetchProducts(queries));
    return result.data;
  }
);
export const reduceUpdateProduct = createAsyncThunk(
  "product/updateProduct",
  async (params, { dispatch, getState }) => {
    const result = await httpRequest({
      url: _urlPrefix + "/update/" + params.id,
      method: "PUT",
      data: AppUtil.toSnakeCaseKey(params),
      successMessage: "toast.update.200"
    });
    if (!result) {
      return;
    }

    const queries = await getState().product.queries;
    dispatch(fetchProducts(queries));
    return result.data;
  }
);
export const reduceDeleteProduct = createAsyncThunk(
  "product/deleteProduct",
  async (params, { dispatch, getState }) => {
    const result = await httpRequest({
      url: _urlPrefix + "/delete/" + params,
      method: "DELETE",
      successMessage: "toast.remove.200"
    });
    if (!result) {
      return;
    }

    const queries = await getState().product.queries;
    dispatch(fetchProducts(queries));
    return result.data;
  }
);

export const fetchPublicProducts = createAsyncThunk(
  "product/fetchPublicProducts",
  async (params) => {
    params = AppUtil.toSnakeCaseKey(params);
    const result = await httpRequest({
      url: _urlPrefix + "/public/page",
      method: "GET",
      params
    });
    if (!result) {
      return;
    }
    // dispatch(setProductQueries(params));
    return result.data;
  }
);
export const fetchPublicProductDetail = createAsyncThunk(
  "product/fetchPublicProductDetail",
  async (params) => {
    const result = await httpRequest({
      url: _urlPrefix + "/public/detail/" + params,
      method: "GET"
    });
    if (!result) {
      return;
    }
    return result.data;
  }
);

const initialState = {
  list: [],
  products: [],
  detail: {
    title: "",
    brandId: 0,
    categoryIds: [],
    categoryProducts: [],
    productImages: [],
    description: ''
  },
  info: {
    title: "",
    brandId: 0,
    categoryIds: [],
    categoryProducts: [],
    description: ''
  },
  selected: [],
  queries: {
    pageNumber: 1,
    pageSize: 10,
    searchKey: "",
    ascSort: true,
    sortField: "createdDate",
    totalRecords: 0,
    totalPages: 0
  },
  publicQueries: {
    pageNumber: 1,
    pageSize: 10,
    searchKey: "",
    ascSort: true,
    sortField: "createdDate",
    totalRecords: 0,
    totalPages: 0
  },
  relatedProducts: [],
  allProducts: []
};

const product = createSlice({
  name: "product",
  initialState: initialState,
  reducers: {
    setProductQueries: (product, action) => {
      product.queries = action.payload;
    },
  },
  extraReducers: {
    [fetchProducts.fulfilled]: (product, action) => {
      if (action.payload) {
        product.list = action.payload.content;
        product.queries.pageNumber = action.payload.pageNumber + 1;
        product.queries.pageSize = action.payload.pageSize;
        product.queries.totalRecords = action.payload.totalElements;
        product.queries.totalPages = action.payload.totalPages;
      }
    },
    [fetchAllProducts.fulfilled]: (product, action) => {
      if (action.payload) {
        product.allProducts = action.payload.content;
      }
    },
    
    [fetchProductDetail.fulfilled]: (product, action) => {
      if (action.payload) {
        product.detail = action.payload;
      }
    },
    [fetchPublicProducts.fulfilled]: (product, action) => {
      if (action.payload) {
        product.products = action.payload.content;
        product.publicQueries.pageNumber = action.payload.pageNumber + 1;
        product.publicQueries.pageSize = action.payload.pageSize;
        product.publicQueries.totalRecords = action.payload.totalElements;
        product.publicQueries.totalPages = action.payload.totalPages;
      }
    },
    [fetchPublicProductDetail.fulfilled]: (product, action) => {
      if (action.payload) {
        product.info = action.payload;
      }
    }
  },
});

const { reducer, actions } = product;
export const { setProductQueries } = actions;
export default reducer;

export const getProducts = createSelector(
  (state) => state.product.list,
  (products) => products
);
export const getAllProducts = createSelector(
  (state) => state.product.allProducts,
  (products) => products
);
export const getProductDetail = createSelector(
  (state) => state.product.detail,
  (product) => product
);
export const getQueries = createSelector(
  (state) => state.product.queries,
  (queries) => queries
);
export const getSelectedProducts = createSelector(
  (state) => state.product.selected,
  (selected) => selected
);

export const getPublicProducts = createSelector(
  (state) => state.product.products,
  (products) => products
);
export const getPublicQueries = createSelector(
  (state) => state.product.publicQueries,
  (queries) => queries
);
export const getPublicProductDetail = createSelector(
  (state) => state.product.info,
  (product) => product
);
